"use client";

import { useCallback, useEffect, useRef, useState } from "react";
import { Button, ButtonLink } from "@/components/ui/Button";

type Checkout = { authorizationId: string; amountMinor: number; state: "pending" | "paid" | "closed" | "refunded" | "review" | "unavailable";
  qrCodeDataUrl: string | null; downloadable: boolean };
const labels = {
  pending: "等待微信付款", paid: "已付款", closed: "原付款已停止", refunded: "已退款",
  review: "订单正在核对或处理退款", unavailable: "付款状态待核对",
};
export function nativePaymentCheckedMessage(state: Checkout["state"]) {
  return state === "pending"
    ? "已刷新原订单，暂未确认付款；若手机已扣款，请勿再次付款。"
    : `已刷新原订单：${labels[state]}。`;
}

export function NativePaymentPanel({ authorizationId, initialCheckout }: { authorizationId: string; initialCheckout?: Checkout }) {
  const firstCheckout = useRef(initialCheckout ?? null);
  const [checkout, setCheckout] = useState<Checkout | null>(initialCheckout ?? null);
  const [error, setError] = useState("");
  const [checkedMessage, setCheckedMessage] = useState("");
  const [busy, setBusy] = useState(false);
  const active = useRef<AbortController | null>(null);
  const lastRead = useRef(0);
  const terminal = useRef(false);
  const refresh = useCallback(async (manual = false) => {
    if (active.current || (!manual && (terminal.current || Date.now() - lastRead.current < 5_000))) return;
    const controller = new AbortController(); active.current = controller; lastRead.current = Date.now();
    const timeout = window.setTimeout(() => controller.abort("timeout"), 15_000);
    setBusy(true); setError(""); if (manual) setCheckedMessage("");
    try {
      const response = await fetch(`/api/v1/cloudbase-authorizations/${encodeURIComponent(authorizationId)}/native-checkout`, {
        method: manual ? "POST" : "GET", cache: "no-store", signal: controller.signal,
      });
      const body = await response.json();
      if (!response.ok) throw new Error(typeof body.message === "string" ? body.message : "原付款暂不可用，请稍后核对。");
      if (body.authorizationId !== authorizationId || !Object.hasOwn(labels, body.state) || !Number.isSafeInteger(body.amountMinor)
        || body.amountMinor <= 0 || typeof body.downloadable !== "boolean"
        || (body.qrCodeDataUrl !== null && (typeof body.qrCodeDataUrl !== "string" || !body.qrCodeDataUrl.startsWith("data:image/png;base64,")))) {
        throw new Error("付款结果不完整，请查看原订单；若已付款请勿重复支付。");
      }
      if (!controller.signal.aborted) {
        terminal.current = body.state !== "pending" && body.state !== "unavailable";
        setCheckout(body);
        setCheckedMessage(manual ? nativePaymentCheckedMessage(body.state as Checkout["state"]) : "");
      }
    } catch (caught) {
      if (!controller.signal.aborted || controller.signal.reason === "timeout") {
        setCheckout(null); // Never retain a stale QR when the order cannot be verified.
        setCheckedMessage("");
        setError(controller.signal.reason === "timeout" ? "查询超时，请稍后核对原单；若已付款请勿重复支付。"
          : caught instanceof Error ? caught.message : "暂时无法读取原付款。");
      }
    } finally {
      window.clearTimeout(timeout);
      if (active.current === controller) active.current = null;
      if (!controller.signal.aborted || controller.signal.reason === "timeout") setBusy(false);
    }
  }, [authorizationId]);
  useEffect(() => {
    lastRead.current = firstCheckout.current ? Date.now() : 0;
    terminal.current = firstCheckout.current ? firstCheckout.current.state !== "pending" : false;
    const readVisible = () => { if (document.visibilityState === "visible") void refresh(); };
    readVisible();
    const timer = window.setInterval(readVisible, 10_000);
    window.addEventListener("focus", readVisible);
    document.addEventListener("visibilitychange", readVisible);
    return () => {
      window.clearInterval(timer); window.removeEventListener("focus", readVisible);
      document.removeEventListener("visibilitychange", readVisible);
      active.current?.abort(); active.current = null;
    };
  }, [refresh]);
  return <section aria-label="原订单微信扫码付款" className="mt-5 text-center">
    <p role="status" className="font-medium">{checkout ? labels[checkout.state] : error ? "暂时无法读取付款" : "正在读取原订单…"}</p>
    {checkout && <p className="mt-2 text-xl">¥{(checkout.amountMinor / 100).toFixed(2)}</p>}
    {checkout?.state === "pending" && checkout.qrCodeDataUrl && <>
      {/* The QR contains only the server-validated original WeChat payment URL. */}
      {/* eslint-disable-next-line @next/next/no-img-element */}
      <img src={checkout.qrCodeDataUrl} alt="微信扫码付款二维码" className="mx-auto mt-4 size-56 max-w-full rounded-xl bg-white p-2" />
    </>}
    {checkout?.state === "pending" && !checkout.qrCodeDataUrl && <p className="mt-3 text-sm">原付款暂不能继续扫码，请先核对付款结果。二维码失效时请在授权中心处理原订单，勿重复提交申请。</p>}
    {checkout?.state === "paid" && <p className="mt-3 text-sm">{checkout.downloadable ? "授权已生效，可到授权中心下载约定素材。" : "已收到付款，请到授权中心查看平台审核与授权进度。"}</p>}
    {checkout && ["review", "closed", "refunded", "unavailable"].includes(checkout.state) && <p className="mt-3 text-sm">请到授权中心查看详情，请勿继续使用旧二维码付款。</p>}
    {error && <p role="alert" className="mt-3 text-sm text-[var(--color-pending)]">{error}</p>}
    {checkedMessage && <p role="status" className="mt-3 text-sm text-[var(--color-muted)]">{checkedMessage}</p>}
    <div className="mt-4 flex flex-wrap justify-center gap-3">
      <Button type="button" variant="secondary" disabled={busy} onClick={() => void refresh(true)}>我已付款</Button>
      {checkout?.state !== "pending" && <ButtonLink href="/partner/authorizations" variant="secondary">查看授权进度</ButtonLink>}
    </div>
  </section>;
}
